/**
 * The company directory in the browser (SA-1456 spec §7.1). Reads sit under `['companies', …]`, every
 * write invalidates that prefix. A search is debounced and carries `asked`, the text its result belongs
 * to, so a caller treats a mismatch as still searching and never shows "no match" inside the debounce.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError, apiClient } from '@/services/apiClient'
import type { Company, CompanyMatch, CompanyRef } from '@/types/companies'
import { useDebouncedValue } from './useDebouncedValue'

export const companiesKey = ['companies'] as const
/** One company's read: callers that already hold the company (a proposal's) seed it here. */
export const companyKey = (id: string | null) => [...companiesKey, 'one', id] as const
export const COMPANY_SEARCH_MIN = 2
export const COMPANY_SEARCH_DEBOUNCE_MS = 250
export const COMPANY_SEARCH_LIMIT = 5

/** The search takes no role: every party field finds every company (the input sorts role holders first). */
export function useCompanySearch(q: string, { enabled = true }: { enabled?: boolean } = {}) {
  const text = useDebouncedValue(q.trim(), COMPANY_SEARCH_DEBOUNCE_MS)
  const query = useQuery({
    queryKey: [...companiesKey, 'search', text],
    // Only for the text on screen: while the debounce lags behind a keystroke the old text is not searched.
    enabled: enabled && text === q.trim() && text.length >= COMPANY_SEARCH_MIN,
    staleTime: 60 * 1000,
    retry: false,
    queryFn: () => {
      const params = new URLSearchParams({ q: text, limit: String(COMPANY_SEARCH_LIMIT) })
      return apiClient.get<CompanyMatch[]>(`/api/companies/search?${params.toString()}`)
    },
  })
  return { matches: query.data ?? [], asked: text, isError: query.isError, isFetching: query.isFetching }
}

/** One company by id, for the chip of a link this tab did not make. */
export function useCompany(id: string | null) {
  return useQuery({
    queryKey: companyKey(id),
    enabled: Boolean(id),
    staleTime: 5 * 60 * 1000,
    retry: false,
    queryFn: () => apiClient.get<CompanyRef>(`/api/companies/${encodeURIComponent(id as string)}`),
  })
}

export interface AddCompanyBody { name: string; roles?: string[]; spelling?: string }

export function useAddCompany() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (body: AddCompanyBody) => apiClient.post<CompanyRef>('/api/companies', body),
    onSuccess: () => client.invalidateQueries({ queryKey: companiesKey }),
  })
}

export function useAddSpelling() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ id, text }: { id: string; text: string }) =>
      apiClient.post<CompanyRef>(`/api/companies/${encodeURIComponent(id)}/spellings`, { text }),
    onSuccess: () => client.invalidateQueries({ queryKey: companiesKey }),
  })
}

// --- The Companies page (spec §7.2): the list, one company, and the manager's writes. ---

/** The list: live companies, the merged ones too with `merged`. The page filters by role itself. */
export function useCompanyList(merged: boolean) {
  return useQuery({
    queryKey: [...companiesKey, 'list', merged],
    queryFn: () => apiClient.get<Company[]>(`/api/companies${merged ? '?merged=1' : ''}`),
  })
}

/** One company, read afresh (the drawer's Reload after a stale save). */
export function readCompany(id: string): Promise<Company> {
  return apiClient.get<Company>(`/api/companies/${encodeURIComponent(id)}`)
}

/** PATCH and an answering DELETE through `apiClient.fetch` (it has neither); a refusal is an `ApiError`. */
async function send<T>(method: 'PATCH' | 'DELETE', path: string, body?: unknown): Promise<T> {
  const response = await apiClient.fetch(path, { method, ...(body === undefined ? {} : { body: JSON.stringify(body) }) })
  if (!response.ok) {
    let detail: unknown = null
    try {
      const parsed = await response.json()
      detail = parsed && typeof parsed === 'object' && 'detail' in parsed ? (parsed as { detail: unknown }).detail : parsed
    } catch {
      // no JSON body: the status alone says what happened
    }
    throw new ApiError(`${method} ${path} failed: ${response.statusText}`, response.status, detail)
  }
  return response.json() as Promise<T>
}

/** A PATCH names only the keys it changes, with the stamp the drawer opened on. */
export type CompanyPatch = Partial<Pick<Company, 'name' | 'short_name' | 'legal_name' | 'country' | 'registration_id' | 'roles'>>

export function usePatchCompany() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ id, patch, expectedUpdatedAt }: { id: string; patch: CompanyPatch; expectedUpdatedAt: string | null }) =>
      send<Company>('PATCH', `/api/companies/${encodeURIComponent(id)}`, { expected_updated_at: expectedUpdatedAt, ...patch }),
    onSuccess: () => client.invalidateQueries({ queryKey: companiesKey }),
  })
}

export function useRemoveSpelling() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ id, spellingId }: { id: string; spellingId: string }) =>
      send<Company>('DELETE', `/api/companies/${encodeURIComponent(id)}/spellings/${encodeURIComponent(spellingId)}`),
    onSuccess: () => client.invalidateQueries({ queryKey: companiesKey }),
  })
}

/** Merge `id` into `intoId`; answers the survivor. */
export function useMergeCompany() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ id, intoId, expectedUpdatedAt }: { id: string; intoId: string; expectedUpdatedAt: string | null }) =>
      apiClient.post<Company>(`/api/companies/${encodeURIComponent(id)}/merge`, { into_id: intoId, expected_updated_at: expectedUpdatedAt }),
    onSuccess: () => client.invalidateQueries({ queryKey: companiesKey }),
  })
}

/** A refusal's `detail.code` and `detail.message`, else nulls. */
export function refusalOf(error: unknown): { code: string | null; message: string | null } {
  const detail = error instanceof ApiError ? error.detail : null
  if (detail && typeof detail === 'object') {
    const { code, message } = detail as { code?: unknown; message?: unknown }
    return { code: typeof code === 'string' ? code : null, message: typeof message === 'string' ? message : null }
  }
  return { code: null, message: null }
}
